// Isolated, stable camera preview. Mounts ONCE, never re-renders (React.memo + empty deps).
// Owns its own zxing reader and video element. Calls onDetect(text, frameDataUrl) once.
import { memo, useEffect, useRef } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

function CameraFeedInner({ onDetect, onReady, onError }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const handledRef = useRef(false);

  const releaseCamera = () => {
    try { controlsRef.current?.stop?.(); } catch { /* noop */ }
    try { readerRef.current?.reset?.(); } catch { /* noop */ }
    const v = videoRef.current;
    if (!v) return;
    const s = v.srcObject;
    if (s?.getTracks) s.getTracks().forEach((tr) => { try { tr.stop(); } catch { /* noop */ } });
    try { v.pause(); } catch { /* noop */ }
    v.srcObject = null;
    try { v.removeAttribute("srcObject"); } catch { /* noop */ }
    try { v.load(); } catch { /* noop */ }
  };

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;
    const grabFrame = () => {
      const v = videoRef.current;
      if (!v || !v.videoWidth) return null;
      const c = document.createElement("canvas");
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0);
      try { return c.toDataURL("image/jpeg", 0.85); } catch { return null; }
    };
    const cb = (result) => {
      if (result && !handledRef.current && !cancelled) {
        handledRef.current = true;
        const frame = grabFrame();
        releaseCamera();
        try { navigator.vibrate?.(120); } catch { /* noop */ }
        onDetect?.(result.getText(), frame);
      }
    };
    reader.decodeFromVideoDevice(undefined, videoRef.current, cb).then((controls) => {
      controlsRef.current = controls;
      if (cancelled || handledRef.current) { releaseCamera(); return; }
      onReady?.();
      const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
      track?.applyConstraints?.({ advanced: [{ focusMode: "continuous" }, { exposureMode: "continuous" }, { whiteBalanceMode: "continuous" }] }).catch(() => {});
    }).catch((e) => { if (!cancelled) onError?.(e.message || "camera"); });
    return () => { cancelled = true; releaseCamera(); }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />;
}

export const CameraFeed = memo(CameraFeedInner, () => true);
